import { useState } from 'react'
import { ErrorBanner, formatUtc, useAsync } from '../../components/Async'
import { Dialog } from '../../components/Dialog'
import { Explainer } from '../../components/Explainer'
import { ApiError, trust } from '../../lib/api'

/**
 * The tenant's signing key.
 *
 * Small page, load-bearing consequence. Every device pins the anchor it was handed at enrollment, so
 * rotating the key means the fleet refuses policy signed with the new one until each device has
 * checked in and picked up the new anchor. That is stated here in as many words, before the button,
 * because it is not recoverable by clicking again.
 *
 * The anchor itself is a public key and is shown in full: it goes into a deployment kit, and an
 * operator building one needs to be able to copy it.
 */
export function TrustPage() {
  const anchor = useAsync(() => trust.anchor(), [])
  const [reason, setReason] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  /**
   * Whether the dialog that asks for this is open.
   *
   * It replaces a reason field that lived on the page and a two-stage button swap that confirmed in
   * place. The dialog is the confirmation: it says what rotating costs, it holds the reason beside
   * the button that acts on it, and closing it is a cancel rather than a half-filled form left on
   * screen. What the rotation does to the fleet has not changed, and is still said before the button.
   */
  const [open, setOpen] = useState(false)

  const { reload } = anchor

  async function rotate() {
    setBusy(true)
    setFailure(null)
    setNotice(null)
    try {
      const result = await trust.rotate(reason || undefined)
      setNotice(
        result.retiredKeyId
          ? `Rotated to ${result.keyId}. ${result.warning ?? ''}`
          : `Signing key ${result.keyId} created. Devices receive it at enrollment.`
      )
      setReason('')
      setOpen(false)
      reload()
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The signing key could not be rotated.')
    } finally {
      setBusy(false)
    }
  }

  const current = anchor.value?.anchor ?? null

  return (
    <>
      <p className="lede">
        The key this tenant signs policy with. Devices pin its public half at enrollment and refuse any
        policy that is not signed by it.
      </p>

      <ErrorBanner message={failure ?? anchor.error} />
      {notice && (
        <div className="banner success" role="status" data-testid="trust-notice">
          {notice}
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>Signing anchor</h3>
          {current ? (
            <dl className="detail" data-testid="signing-anchor">
              <dt>Key id</dt>
              <dd className="mono">{current.keyId}</dd>
              <dt>Algorithm</dt>
              <dd>
                {current.algorithm} · {current.keySizeBits} bits
              </dd>
              <dt>Active since</dt>
              <dd>{formatUtc(current.activatedUtc)}</dd>
              <dt>Public key</dt>
              <dd className="mono" style={{ wordBreak: 'break-all' }}>
                {current.publicKeyBase64}
              </dd>
            </dl>
          ) : (
            <div className="banner" data-testid="no-anchor">
              This tenant has no signing key, so encrypted policy cannot be issued and device feeds
              are empty. Create one below.
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>{current ? 'Rotate the signing key' : 'Create a signing key'}</h3>
          {/* Stays on the page rather than moving into the dialog. It is the reason somebody would
              decide not to do this, and a warning you only see after asking is a warning too late. */}
          {current && (
            <div className="banner" role="note" data-testid="rotation-warning">
              Rotating replaces the anchor every enrolled device pinned. Each device picks the new one
              up at its next check-in, and until then refuses policy signed with the new key.
            </div>
          )}

          <button
            type="button"
            className="primary"
            onClick={() => setOpen(true)}
            disabled={busy}
            data-testid="rotate-signing-key"
          >
            {current ? 'Rotate signing key' : 'Create signing key'}
          </button>
        </div>
      </div>

      <Explainer summary="What pinning means, and what a rotation costs" data-testid="trust-explainer">
        <p className="muted" style={{ marginTop: 0 }}>
          Every device is handed this key's public half when it enrolls, and stores it. From then on it
          verifies every policy envelope against that stored copy and refuses anything signed by
          anything else. Nothing on this server can reach into a machine and change the copy it holds.
        </p>
        <p className="muted">
          So a rotation is not applied, it is offered: each device picks the new anchor up at its next
          check-in, and until it does, it refuses policy signed with the new key. How long that window
          is comes straight off the Check-in tab — a fleet polling twice a day is wrong for up to
          twelve hours, and one polling weekly is wrong for a week.
        </p>
        <p className="hint">
          The public key is shown in full because it goes into a deployment kit and somebody building
          one needs to copy it. It is a public key: there is nothing here to keep secret.
        </p>
      </Explainer>

      <Dialog
        open={open}
        title={current ? 'Rotate the signing key' : 'Create a signing key'}
        onClose={() => setOpen(false)}
        data-testid="rotate-dialog"
      >
        <p className="muted" style={{ marginTop: 0 }}>
          {current
            ? 'Every enrolled device pinned the anchor this replaces, and refuses policy signed with the new key until it has checked in and picked the new one up.'
            : 'Without a signing key this tenant can issue no encrypted policy, and its device feeds are empty.'}
        </p>
        <form
          data-testid="rotate-form"
          onSubmit={(event) => {
            event.preventDefault()
            void rotate()
          }}
        >
          <div className="field">
            <label htmlFor="rotate-reason">Reason</label>
            <input
              id="rotate-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Scheduled rotation"
              data-testid="rotate-reason"
            />
            <div className="hint">Kept with the key, and read by whoever asks later why the fleet went quiet.</div>
          </div>

          <div className="dialog-actions">
            <button type="button" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="primary" disabled={busy} data-testid="rotate-confirm">
              {current ? 'Yes, rotate now' : 'Create signing key'}
            </button>
          </div>
        </form>
      </Dialog>
    </>
  )
}
